import { defineStore } from "pinia";
import { computed, reactive, ref } from "vue";

import type { I_it4CreateContract_Return } from "@/hooks/requests/it4CreateContract/it4CreateContract.types";

import { destructureFIO } from "@/helpers/destructureFIO";
import { useTIWStore } from "@/stores/tiw/tiw";

import type { I_Fields } from "./successfulEvaluation.types";

/**
 * * 🆂🆃🅾🆁🅴 [Successful Evaluation]
 * */
export const useSuccessfulEvaluationStore = defineStore("successfulEvaluation", () => {
    const TIWStore = useTIWStore();

    // |-------|
    // | STATE |
    // |-------|

    /** Значения полей для fetch-отправки формы */
    const fields = reactive<I_Fields>({
        fio: "",
        inn: "",
        passportSeriesNumber: "",
        passportIssueDate: "",
        email: "",
        phone: "",
        vatForm: false,
        personal: false,
        duty: false,
        // acceptContract: false,
        docNum: "",
        docApartmentNum: "",
        docHouseNum: "",
        docDistrict: "",
        docPostCode: "",
        docStreet: "",
        docCityName: "",
    });

    const contractData = ref<null | I_it4CreateContract_Return["data"]>(null);

    const isEmailRequired = TIWStore.domainIsComfy || TIWStore.domainIsTakealot;
    // FIXME: как будто эту логику можно завязать на валидациях полей, а не выкатывать сюда, в отдельную сущность.
    /** Выбраны ли все Select ручного ввода характеристик */
    const isAcceptedToNext = computed<boolean>(() => {
        // 1. БАЗА: Поля, которые должны быть заполнены всегда
        const requiredKeys: (keyof I_Fields)[] = ["fio", "phone", "personal"];

        // 2. БЛОК СКИДКИ (Обязательно, если оформляем сразу)
        if (!TIWStore.isDiscountLater) {
            requiredKeys.push("duty");

            // ИНН (в Молдове IDNP) нужен всем, кроме ЮАР
            if (!TIWStore.locationIsZA) {
                requiredKeys.push("inn");
            }

            // Паспорт нужен только для Украины и теперь для Молдовы
            if (TIWStore.locationIsUA) {
                requiredKeys.push("passportSeriesNumber", "passportIssueDate");
            }
        }

        // 3. БЛОК ЮАР (Адресные данные)
        if (TIWStore.locationIsZA) {
            requiredKeys.push(
                "docNum",
                "docHouseNum",
                "docDistrict",
                "docPostCode",
                "docStreet",
                "docCityName",
                "vatForm",
            );
        }
        if (TIWStore.locationIsMD) {
            requiredKeys.push("docNum", "docCityName", "docStreet", "docHouseNum");
        }

        // 4. БЛОК COMFY (Email)
        if (isEmailRequired) {
            requiredKeys.push("email");
        }

        // 5. ПРОВЕРКА
        return requiredKeys.every((key) => {
            const val = fields[key];
            // Для чекбоксов (personal, duty) проверяем на true
            if (typeof val === "boolean") return val === true;
            // Для строк проверяем, что не пусто
            return Boolean(val && String(val).trim() !== "");
        });
    });

    const destructuredFio = computed(() => destructureFIO(fields.fio));

    // |---------|
    // | METHODS |
    // |---------|

    /** Сбросить значения STORE на по-умолчанию */
    const restoreToDefault = () => {
        let property: keyof I_Fields;
        for (property in fields) {
            if (Object.prototype.hasOwnProperty.call(fields, property)) {
                if (
                    property === "personal" ||
                    property === "duty" ||
                    property === "vatForm"
                    // || property === "acceptContract"
                ) {
                    fields[property] = false;
                    return;
                }
                fields[property] = "";
            }
        }
        contractData.value = null;
    };

    return {
        // * 🆂🆃🅾🆁🅴
        fields,
        contractData,

        isEmailRequired,

        // * 🅲🅾🅼🅿🆄🆃🅴🅳
        destructuredFio,
        isAcceptedToNext,

        // * 🅼🅴🆃🅷🅾🅳🆂
        restoreToDefault,
    };
});
